import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TextButton } from '@xingternal/button';
import { trackEvent } from '@xingternal/hops-tracking';
import { IconArrowDown, IconArrowUp } from '@xingternal/icons';
import { BodyCopy, Headline } from '@xingternal/typography';

import { KununuLink } from '@/components/kununu';
import { CompanyMappedBenefit } from '@/graphql/generated';

import { KununuData } from '../../company-module/employer-actions';
import { getFilteredList, orderBenefits } from './benefits-helpers';

import * as S from './benefits.styles';

const BENEFIT_PREFIX = 'COMPANY_BENEFIT_';

type Props = {
  kununuData: KununuData;
  isCollapsingEnabled: boolean;
};

export type BenefitWithLabel = CompanyMappedBenefit & {
  label: string;
};

export const Benefits: React.FC<Props> = ({ kununuData, isCollapsingEnabled }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { formatMessage } = useIntl();
  const { companyProfileUrl, mappedBenefits } = kununuData;

  const onKununuLinkClick = (): void =>
    trackEvent('PropTrackAction', { PropTrackAction: 'jobs_company_module_kununu_logo_click' });

  const getMappedBenefitsReady = React.useMemo((): BenefitWithLabel[] => {
    if (mappedBenefits) {
      const filteredBenefits = mappedBenefits.filter((benefit) => !!benefit) as CompanyMappedBenefit[];
      return filteredBenefits.map((benefit) => {
        return { ...benefit, label: formatMessage({ id: `COMPANY_BENEFIT_${benefit.type}` }) };
      });
    }
    return [];
  }, [formatMessage, mappedBenefits]);

  const benefits = React.useMemo(() => {
    return getFilteredList(orderBenefits(getMappedBenefitsReady), isCollapsingEnabled, isExpanded);
  }, [getMappedBenefitsReady, isCollapsingEnabled, isExpanded]);

  return (
    <>
      {benefits.length > 0 ? (
        <S.StyledTagGroupWithHeadline>
          <Headline size="large">
            <FormattedMessage id="JOBS_JOB_DETAIL_KUNUNU_BENEFITS" />
          </Headline>
          <S.StyledTagGroup>
            {benefits.map((benefit) => (
              <S.StyledTag key={benefit.type} data-testid="benefit-item">
                <BodyCopy size="medium" noMargin>
                  <FormattedMessage id={`${BENEFIT_PREFIX}${benefit.type}`} />
                </BodyCopy>
              </S.StyledTag>
            ))}
            {!isCollapsingEnabled && benefits.length >= 4 ? (
              <KununuLink companyProfileUrl={companyProfileUrl} onClick={onKununuLinkClick}>
                <S.StyledTag fontWeight="bold">
                  <FormattedMessage id="JOB_DETAILS_KUNUNU_BENEFITS_SEE_MORE" />
                  ...
                </S.StyledTag>
              </KununuLink>
            ) : null}
          </S.StyledTagGroup>
        </S.StyledTagGroupWithHeadline>
      ) : null}
      {isCollapsingEnabled && benefits.length >= 4 ? (
        <TextButton
          icon={isExpanded ? IconArrowUp : IconArrowDown}
          iconAfter
          onClick={() => setIsExpanded((prev) => !prev)}
          size="small"
          data-testid="collapsing-button"
        >
          <FormattedMessage
            id={isExpanded ? 'JOBS_JOB_DETAIL_KUNUNU_BENEFITS_SHOW_LESS' : 'JOBS_JOB_DETAILS_KUNUNU_BENEFITS_SHOW_ALL'}
          />
        </TextButton>
      ) : null}
    </>
  );
};
